// Sokoban Demo - キーボード操作でキャラクターを動かすデモ

import { useRef, useEffect, useCallback, useState, useMemo } from "react";
import { MapLoader } from "engine/map/MapLoader";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { performMove } from "engine/character/animations/move";
import { Animation } from "@babylonjs/core/Animations/animation";
import {
  GameEngine,
  MapRenderer,
  Character,
  TileRegistry,
  registerDefaultTiles,
  TileCategory,
  Direction,
  MoveResult,
} from "engine";
import type { MapData, CharacterDefinition, Vector3D } from "engine";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";

// 方向から移動ベクトルを取得
function getForwardVector(direction: Direction): { x: number; z: number } {
  switch (direction) {
    case Direction.POSITIVE_X:
      return { x: 1, z: 0 };
    case Direction.NEGATIVE_X:
      return { x: -1, z: 0 };
    case Direction.POSITIVE_Z:
      return { x: 0, z: 1 };
    case Direction.NEGATIVE_Z:
      return { x: 0, z: -1 };
    default:
      return { x: 0, z: 0 };
  }
}

// 待機用ヘルパー
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// カスタムタイルを登録（箱用・ゴール用）
function registerSokobanTiles(registry: TileRegistry): void {
  // 押せる箱 - offset.y = 1.0 で地面の上に配置
  registry.registerTile({
    id: "tileLow_teamRed",
    name: "Low Tile (Red) - Box",
    filename: "tileLow_teamRed.gltf.glb",
    offset: { x: 0, y: 1.0, z: 0 },
    size: { x: 1, y: 1, z: 1 },
    category: TileCategory.OBSTACLE,
    collides: true,
    walkable: false,
  });

  // ゴール（ボタン） - offset.y = 1.0 で地面の上に配置
  // デフォルト登録をオーバーライド
  registry.registerTile({
    id: "button_teamRed",
    name: "Button (Red) - Goal",
    filename: "button_teamRed.gltf.glb",
    offset: { x: 0, y: 1.0, z: 0 },
    size: { x: 1.6, y: 0.3, z: 1.6 },
    category: TileCategory.GROUND,
    collides: false,
    walkable: true,
  });
}

// デモ用マップデータ
const demoMapData: MapData = {
  width: 5,
  height: 5,
  layers: 1,
  tileSize: 2,
  tiles: [
    // 壁（外周）- tileHigh_forest
    // 上の行
    { tileType: "tileHigh_forest", x: 0, y: 0, z: 0 },
    { tileType: "tileHigh_forest", x: 1, y: 0, z: 0 },
    { tileType: "tileHigh_forest", x: 2, y: 0, z: 0 },
    { tileType: "tileHigh_forest", x: 3, y: 0, z: 0 },
    { tileType: "tileHigh_forest", x: 4, y: 0, z: 0 },
    // 下の行
    { tileType: "tileHigh_forest", x: 0, y: 0, z: 4 },
    { tileType: "tileHigh_forest", x: 1, y: 0, z: 4 },
    { tileType: "tileHigh_forest", x: 2, y: 0, z: 4 },
    { tileType: "tileHigh_forest", x: 3, y: 0, z: 4 },
    { tileType: "tileHigh_forest", x: 4, y: 0, z: 4 },
    // 左の列
    { tileType: "tileHigh_forest", x: 0, y: 0, z: 1 },
    { tileType: "tileHigh_forest", x: 0, y: 0, z: 2 },
    { tileType: "tileHigh_forest", x: 0, y: 0, z: 3 },
    // 右の列
    { tileType: "tileHigh_forest", x: 4, y: 0, z: 1 },
    { tileType: "tileHigh_forest", x: 4, y: 0, z: 2 },
    { tileType: "tileHigh_forest", x: 4, y: 0, z: 3 },

    // 床（内部）- tileLow_forest
    { tileType: "tileLow_forest", x: 1, y: 0, z: 1 },
    { tileType: "tileLow_forest", x: 2, y: 0, z: 1 },
    { tileType: "tileLow_forest", x: 3, y: 0, z: 1 },
    { tileType: "tileLow_forest", x: 1, y: 0, z: 2 },
    { tileType: "tileLow_forest", x: 2, y: 0, z: 2 },
    { tileType: "tileLow_forest", x: 3, y: 0, z: 2 },
    { tileType: "tileLow_forest", x: 1, y: 0, z: 3 },
    { tileType: "tileLow_forest", x: 2, y: 0, z: 3 },
    { tileType: "tileLow_forest", x: 3, y: 0, z: 3 },

    // ゴール位置（ボタン）- button_teamRed
    { tileType: "button_teamRed", x: 3, y: 0, z: 2 },

    // 箱 - tileLow_teamRed
    { tileType: "tileLow_teamRed", x: 2, y: 0, z: 2 },
  ],
  characters: [
    {
      characterType: "duck",
      x: 1,
      y: 0,
      z: 2,
      direction: Direction.POSITIVE_X, // 右向き
      isPlayer: true,
    },
  ],
  goals: [],
};

// キャラクター定義
const duckDefinition: CharacterDefinition = {
  id: "duck",
  name: "Duck",
  filename: "character_duck.glb",
  offset: { x: 0, y: 0, z: 0 },
  scaling: { x: 1.2, y: 1.2, z: -1.2 },
};

export function SokobanDemo() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const characterRef = useRef<Character | null>(null);
  const mapRendererRef = useRef<MapRenderer | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [message, setMessage] = useState("Loading...");

  // 箱の位置を追跡（key: "x,z" 形式、value: tileId）
  // 箱はtiles配列の27番目（index 26）
  const [boxPositions, setBoxPositions] = useState<Map<string, string>>(
    () => new Map([["2,2", "tile_26"]]),
  );

  // 箱のアニメーション状態
  const [isBoxAnimating, setIsBoxAnimating] = useState(false);

  // 壁の位置（固定）
  const wallPositions = useMemo(
    () =>
      new Set([
        "0,0",
        "1,0",
        "2,0",
        "3,0",
        "4,0", // 上の行
        "0,4",
        "1,4",
        "2,4",
        "3,4",
        "4,4", // 下の行
        "0,1",
        "0,2",
        "0,3", // 左の列
        "4,1",
        "4,2",
        "4,3", // 右の列
      ]),
    [],
  );

  // ゴール位置
  const goalPosition = useMemo(() => ({ x: 3, z: 2 }), []);

  // 初期化
  useEffect(() => {
    const initializeGame = async () => {
      if (!canvasRef.current) return;

      try {
        // エンジンを初期化
        const engine = GameEngine.getInstance({ debug: true });
        engineRef.current = engine;

        await engine.initialize(canvasRef.current, {
          alpha: 10,
          beta: 1.0,
          radius: 15,
          target: { x: 4, y: 0, z: 4 },
        });

        const scene = engine.getScene();
        const eventBus = engine.getEventBus();

        if (!scene) {
          throw new Error("Scene not initialized");
        }

        // タイルレジストリを作成
        const tileRegistry = new TileRegistry();
        registerDefaultTiles(tileRegistry);
        registerSokobanTiles(tileRegistry);

        // マップレンダラーを作成
        const mapRenderer = new MapRenderer(scene, eventBus, { tileRegistry });
        mapRendererRef.current = mapRenderer;

        // マップを読み込んでレンダリング
        const mapLoader = new MapLoader();
        const mapConfig = mapLoader.loadFromData(demoMapData, "sokoban-demo", "Sokoban Demo");
        await mapRenderer.render(mapConfig);

        // キャラクターを作成
        const charData = demoMapData.characters[0];
        const character = new Character(
          scene,
          eventBus,
          duckDefinition,
          { x: charData.x, y: charData.y, z: charData.z },
          charData.direction as Direction,
        );
        characterRef.current = character;
        await character.load();

        // アニメーション速度を上げる
        character.setSpeedMultiplier(2);

        // ゲーム開始
        engine.start();
        setIsReady(true);
        setMessage("矢印キーで移動、Q/Eで回転");
      } catch (error) {
        console.error("Failed to initialize:", error);
        setMessage("初期化に失敗しました");
      }
    };

    initializeGame();

    return () => {
      // クリーンアップ
      characterRef.current?.dispose();
      mapRendererRef.current?.dispose();
      GameEngine.resetInstance();
    };
  }, []);

  // 滑らかな移動（ホッピングなし）- 箱押し時のキャラクター用
  const performSlideMove = useCallback(async (node: TransformNode, targetPosition: Vector3) => {
    const scene = engineRef.current?.getScene();
    if (!scene) return;

    const frames = 2;

    const moveAnimation = new Animation(
      "slideMove",
      "position",
      frames,
      Animation.ANIMATIONTYPE_VECTOR3,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );

    const startPosition = node.position.clone();
    const animationKeys = [];

    for (let i = 0; i <= frames; i++) {
      const t = i / frames;
      const x = startPosition.x + (targetPosition.x - startPosition.x) * t;
      const z = startPosition.z + (targetPosition.z - startPosition.z) * t;
      // Y座標は固定（ホッピングなし）
      const y = startPosition.y;

      animationKeys.push({
        frame: i,
        value: new Vector3(x, y, z),
      });
    }

    animationKeys[animationKeys.length - 1].value = targetPosition.clone();
    moveAnimation.setKeys(animationKeys);

    const animation = scene.beginDirectAnimation(node, [moveAnimation], 0, frames, false, 2);

    await animation.waitAsync();
  }, []);

  // 箱を押す関数（3Dモデルも移動）
  const pushBox = useCallback(
    async (oldKey: string, newKey: string, newPos: Vector3D) => {
      const tileId = boxPositions.get(oldKey);
      if (!tileId) return;

      const mapRenderer = mapRendererRef.current;
      const engine = engineRef.current;
      if (!mapRenderer || !engine) return;

      const tileNode = mapRenderer.getRenderedTile(tileId);
      const scene = engine.getScene();
      if (!tileNode || !scene) return;

      setIsBoxAnimating(true);

      // State更新
      setBoxPositions((prev) => {
        const next = new Map(prev);
        next.delete(oldKey);
        next.set(newKey, tileId);
        return next;
      });

      // アニメーションで移動
      const targetWorldPos = new Vector3(
        newPos.x * 2,
        tileNode.position.y, // Y座標は維持
        newPos.z * 2,
      );

      await performMove(scene, tileNode, targetWorldPos, {
        framesPerSecond: 3,
        speedMultiplier: 2,
      });

      setIsBoxAnimating(false);
    },
    [boxPositions],
  );

  // 移動可能判定（純粋な判定関数 - 箱を押さない）
  const canMove = useCallback(
    (targetPos: Vector3D): MoveResult => {
      // 箱がアニメーション中は移動不可
      if (isBoxAnimating) {
        return MoveResult.BLOCKED;
      }

      const targetKey = `${targetPos.x},${targetPos.z}`;

      // 1. 壁チェック
      if (wallPositions.has(targetKey)) {
        return MoveResult.BLOCKED;
      }

      // 2. 箱チェック（箱がある場合はBLOCKED）
      if (boxPositions.has(targetKey)) {
        return MoveResult.BLOCKED;
      }

      return MoveResult.SUCCESS;
    },
    [boxPositions, wallPositions, isBoxAnimating],
  );

  // 箱を押せるか判定
  const canPushBox = useCallback(
    (
      targetPos: Vector3D,
      charPos: Vector3D,
    ): { canPush: boolean; targetKey: string; boxNewKey: string; boxNewPos: Vector3D } | null => {
      const targetKey = `${targetPos.x},${targetPos.z}`;

      // 箱がない場合はnull
      if (!boxPositions.has(targetKey)) {
        return null;
      }

      const pushDirX = targetPos.x - charPos.x;
      const pushDirZ = targetPos.z - charPos.z;

      // 箱の先の位置
      const boxNewPos = {
        x: targetPos.x + pushDirX,
        y: 0,
        z: targetPos.z + pushDirZ,
      };
      const boxNewKey = `${boxNewPos.x},${boxNewPos.z}`;

      // 箱の先に障害物があるかチェック
      if (wallPositions.has(boxNewKey) || boxPositions.has(boxNewKey)) {
        return { canPush: false, targetKey, boxNewKey, boxNewPos };
      }

      return { canPush: true, targetKey, boxNewKey, boxNewPos };
    },
    [boxPositions, wallPositions],
  );

  // ゴール判定
  useEffect(() => {
    if (boxPositions.has(`${goalPosition.x},${goalPosition.z}`)) {
      setMessage("クリア！箱をゴールに運びました");
    }
  }, [boxPositions, goalPosition]);

  // キーボードイベント
  useEffect(() => {
    if (!isReady) return;

    const handleKeyDown = async (e: KeyboardEvent) => {
      const character = characterRef.current;
      if (!character) return;

      // アニメーション中は無視
      if (character.getState().isAnimating || isBoxAnimating) return;

      switch (e.key) {
        case "ArrowUp":
        case "w":
        case "W": {
          // 移動先を計算
          const charState = character.getState();
          const forward = getForwardVector(charState.direction);
          const targetPos = {
            x: charState.position.x + forward.x,
            y: 0,
            z: charState.position.z + forward.z,
          };

          // 壁チェック
          const targetKey = `${targetPos.x},${targetPos.z}`;
          if (wallPositions.has(targetKey)) {
            return; // 壁があるので移動不可
          }

          // 箱を押せるか判定
          const pushResult = canPushBox(targetPos, charState.position);

          if (pushResult) {
            // 箱がある場合
            if (!pushResult.canPush) {
              return; // 箱を押せない（先に障害物）
            }

            // 1. 箱を押す（awaitしない - 並行実行）
            pushBox(pushResult.targetKey, pushResult.boxNewKey, pushResult.boxNewPos);

            // 2. 少し待機（箱が先に動き始める時間を確保）
            await delay(300);

            // 3. キャラクターを滑らかに移動（ホッピングなし）
            const charNode = character.getRootNode();
            if (charNode) {
              const targetWorldPos = new Vector3(
                targetPos.x * 2,
                charNode.position.y,
                targetPos.z * 2,
              );
              await performSlideMove(charNode, targetWorldPos);
              // 内部状態を更新
              character.setPosition(targetPos);
            }
          } else {
            // 箱がない場合は通常移動
            await character.moveForward(canMove);
          }
          break;
        }
        case "ArrowLeft":
        case "a":
        case "A":
          await character.turnLeft();
          break;
        case "ArrowRight":
        case "d":
        case "D":
          await character.turnRight();
          break;
        case "q":
        case "Q":
          await character.turnLeft();
          break;
        case "e":
        case "E":
          await character.turnRight();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isReady, canMove, canPushBox, pushBox, wallPositions, isBoxAnimating, performSlideMove]);

  return (
    <div style={{ width: "100%", height: "100vh", position: "relative" }}>
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
      <div
        style={{
          position: "absolute",
          top: 20,
          left: "50%",
          transform: "translateX(-50%)",
          color: "white",
          backgroundColor: "rgba(0,0,0,0.5)",
          padding: "10px 20px",
          borderRadius: 8,
          fontFamily: "sans-serif",
          textAlign: "center",
        }}
      >
        <h2 style={{ margin: 0, marginBottom: 8 }}>Sokoban Demo</h2>
        <p style={{ margin: 0 }}>{message}</p>
      </div>
    </div>
  );
}

export default SokobanDemo;
